/**
 * FicheTechniqueTab — Nouvel écran du profil Cuisinier (Fév 2026)
 *
 * Workflow :
 *  1. Bons de commande cuisine (Pending / In-progress / Completed) — vue par onglets
 *  2. Auto-remplissage des ingrédients depuis la fiche technique (idée A)
 *  3. Cases à cocher + saisie de la quantité RÉELLE utilisée (avec écart affiché)
 *  4. Chronomètre auto (idée F) — démarre au "Commencer"
 *  5. Photo QC du plat fini (idée G)
 *  6. Batch cooking (idée E) — batch_size affiché
 *  7. Validation → décrément stock immédiat, réversible admin (règle 3c)
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ChefHat, PlayCircle, CheckCircle2, Clock, Camera, Package,
  Loader2, RefreshCw, Timer, Utensils, X, Plus, AlertCircle,
  Trophy, Copy, Archive, StopCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatTime = (s) => {
  if (!s || s < 0) return "—";
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
};

// Chrono live : (started_at ISO) → durée en secondes
const useLiveElapsed = (startedIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!startedIso) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedIso]);
  if (!startedIso) return null;
  try {
    const started = new Date(startedIso).getTime();
    return Math.max(0, Math.floor((now - started) / 1000));
  } catch { return null; }
};

const FicheTechniqueTab = ({ currentUser }) => {
  const [tab, setTab] = useState("pending"); // pending | in_progress | completed
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, in_progress: 0, completed: 0 });
  const [loading, setLoading] = useState(false);
  const [openOrder, setOpenOrder] = useState(null); // detailed dialog
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]);
  const [newForm, setNewForm] = useState({
    dish_name: "", recipe_id: "", batch_size: 1, notes: "", priority: "normal",
  });

  // 🆕 (Fév 2026) Auto-bascule sur "En cours" si aucun bon en attente MAIS bon en cours
  useEffect(() => {
    if (tab === "pending" && (counts.pending || 0) === 0 && (counts.in_progress || 0) > 0) {
      setTab("in_progress");
    }
  }, [counts.pending, counts.in_progress, tab]);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/kitchen-orders`, { params: { status: tab } });
      setOrders(r.data?.orders || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error("Erreur de chargement des bons cuisine");
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh toutes les 30s en pending/in_progress
  useEffect(() => {
    if (tab === "completed") return;
    const t = setInterval(fetchOrders, 30000);
    return () => clearInterval(t);
  }, [tab, fetchOrders]);

  // Load recipes + products for the "new bon" dialog
  useEffect(() => {
    (async () => {
      try {
        const r1 = await axios.get(`${API}/stock/recipes`);
        setRecipes(Array.isArray(r1.data) ? r1.data : (r1.data?.recipes || []));
      } catch { /* silent */ }
      try {
        const r2 = await axios.get(`${API}/stock/products`);
        setProducts(Array.isArray(r2.data) ? r2.data : (r2.data?.products || []));
      } catch { /* silent */ }
    })();
  }, []);

  const createOrder = async () => {
    if (!newForm.dish_name.trim()) {
      toast.error("Nom du plat requis");
      return;
    }
    try {
      const r = await axios.post(`${API}/kitchen-orders`, {
        ...newForm,
        batch_size: Math.max(1, parseInt(newForm.batch_size || 1)),
        created_by_role: currentUser?.role,
        created_by_name: currentUser?.full_name || currentUser?.username,
      });
      toast.success(`Bon "${newForm.dish_name}" créé`);
      setNewOrderOpen(false);
      setNewForm({ dish_name: "", recipe_id: "", batch_size: 1, notes: "", priority: "normal" });
      fetchOrders();
      // Ouvre directement pour permettre de commencer
      setOpenOrder(r.data?.order);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const TABS_DEF = [
    { key: "pending", label: "En attente", icon: Clock, color: "amber", count: counts.pending || 0 },
    { key: "in_progress", label: "En cours", icon: PlayCircle, color: "cyan", count: counts.in_progress || 0 },
    { key: "completed", label: "Terminés", icon: CheckCircle2, color: "emerald", count: counts.completed || 0 },
    { key: "aborted", label: "Archivés", icon: Archive, color: "slate", count: counts.aborted || 0 },
  ];

  return (
    <div className="space-y-3" data-testid="fiche-technique-tab">
      {/* Header */}
      <Card className="bg-gradient-to-br from-amber-950/40 to-orange-950/30 border-amber-500/40">
        <CardHeader className="pb-2">
          <CardTitle className="text-amber-200 flex items-center justify-between gap-2 text-base">
            <span className="flex items-center gap-2">
              <ChefHat className="w-5 h-5" />
              Fiche Technique — Bons de commande cuisine
            </span>
            <div className="flex items-center gap-2">
              {(currentUser?.role === "admin" || currentUser?.role === "manager") && (
                <Button
                  onClick={() => setNewOrderOpen(true)}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="new-kitchen-order-btn"
                  title="Création manuelle — réservé admin/manager"
                >
                  <Plus className="w-4 h-4 mr-1" /> Nouveau bon
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={fetchOrders}
                disabled={loading}
                className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="ft-refresh-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2 border-t border-amber-500/20 text-xs text-amber-200/80">
          Chaque bon reprend automatiquement les ingrédients de la fiche technique (recette). Cochez au fur et à mesure de la préparation.
          En fin, cliquez sur <strong>Terminer</strong> — les quantités seront décrémentées du stock. L&apos;admin peut annuler un bon complété si besoin.
        </CardContent>
      </Card>

      {/* Sous-onglets */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {TABS_DEF.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`ft-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                active
                  ? `bg-${t.color}-500/25 text-${t.color}-100 border border-${t.color}-500/50 ring-2 ring-${t.color}-500/20`
                  : "bg-slate-800/60 text-slate-400 border border-slate-700 hover:text-white"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
              <span className="opacity-70 font-mono">({t.count})</span>
            </button>
          );
        })}
      </div>

      {/* Liste des bons */}
      {orders.length === 0 && !loading ? (
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="py-12 text-center">
            <Utensils className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500">
              {tab === "pending" ? "Aucun bon en attente 🎉"
                : tab === "in_progress" ? "Aucun bon en cours"
                : tab === "aborted" ? "Aucun bon archivé"
                : "Aucun bon terminé pour le moment"}
            </p>
            <p className="text-slate-600 text-xs mt-2">
              {tab === "pending" ? "Créez un nouveau bon ou attendez qu'un manager en crée."
                : tab === "aborted" ? "Les bons arrêtés en cours de préparation apparaîtront ici."
                : "Passez à un autre onglet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.flatMap((o) => {
            // 🆕 (Fév 2026) Éclatement : une bulle par plat (dish) du bon.
            // Si le bon a plusieurs dishes, chaque plat devient sa propre carte.
            // Toutes les actions (start, complete, abort, delete) s'appliquent au bon parent.
            const dishes = Array.isArray(o.dishes) && o.dishes.length > 0
              ? o.dishes
              : [{ name: o.dish_name || "Plat", quantity: 1 }];
            return dishes.map((dish, dishIdx) => (
              <DishBubble
                key={`${o.id}-${dishIdx}`}
                order={o}
                dish={dish}
                dishIndex={dishIdx}
                totalDishes={dishes.length}
                onOpen={() => setOpenOrder(o)}
                currentUser={currentUser}
                onDeleted={fetchOrders}
                onAborted={fetchOrders}
              />
            ));
          })}
        </div>
      )}

      {/* Dialog détail bon */}
      <OrderDetailDialog
        order={openOrder}
        onClose={() => setOpenOrder(null)}
        onChanged={async () => {
          fetchOrders();
          // Rafraîchit également l'openOrder pour que le dialog se mette à jour
          if (openOrder?.id) {
            try {
              const r = await axios.get(`${API}/kitchen-orders/${openOrder.id}`);
              if (r.data?.order) setOpenOrder(r.data.order);
            } catch { /* silent */ }
          }
        }}
        currentUser={currentUser}
        products={products}
      />

      {/* Dialog nouveau bon */}
      <Dialog open={newOrderOpen} onOpenChange={setNewOrderOpen}>
        <DialogContent className="bg-slate-900 border-amber-500/40 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center gap-2">
              <ChefHat className="w-5 h-5" /> Nouveau bon de commande cuisine
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Créer un bon manuellement (l&apos;auto-remplissage se fait via la recette).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-slate-300 text-xs uppercase font-bold">Plat / Nom du bon</Label>
              <Input
                value={newForm.dish_name}
                onChange={(e) => setNewForm((p) => ({ ...p, dish_name: e.target.value }))}
                placeholder="Ex: Chawarma poulet"
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="new-order-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase font-bold">Fiche technique (recette)</Label>
              <select
                value={newForm.recipe_id}
                onChange={(e) => setNewForm((p) => ({ ...p, recipe_id: e.target.value }))}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded px-2 py-2 mt-1 text-sm"
                data-testid="new-order-recipe"
              >
                <option value="">— Aucune (saisie manuelle) —</option>
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>{r.name} ({(r.ingredients || []).length} ing.)</option>
                ))}
              </select>
              <p className="text-[10px] text-slate-500 mt-1">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Idée A : les ingrédients de la recette seront pré-cochés automatiquement.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-slate-300 text-xs uppercase font-bold">Portions (batch)</Label>
                <Input
                  type="number"
                  min="1"
                  value={newForm.batch_size}
                  onChange={(e) => setNewForm((p) => ({ ...p, batch_size: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="new-order-batch"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase font-bold">Priorité</Label>
                <select
                  value={newForm.priority}
                  onChange={(e) => setNewForm((p) => ({ ...p, priority: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 text-white rounded px-2 py-2 mt-1 text-sm"
                >
                  <option value="urgent">🔥 Urgente</option>
                  <option value="normal">Normale</option>
                  <option value="low">Basse</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase font-bold">Notes (optionnel)</Label>
              <Textarea
                value={newForm.notes}
                onChange={(e) => setNewForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Ex: Sans piment, extra fromage…"
                className="bg-slate-800 border-slate-700 text-white mt-1"
                rows={2}
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={() => setNewOrderOpen(false)} className="flex-1 border-slate-600 text-slate-300">Annuler</Button>
            <Button onClick={createOrder} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="new-order-submit">
              <Plus className="w-4 h-4 mr-1" /> Créer le bon
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== DISH BUBBLE (🆕 Fév 2026 — une bulle par plat) ====================
const DishBubble = ({ order, dish, dishIndex, totalDishes, onOpen, currentUser, onDeleted, onAborted }) => {
  const elapsed = useLiveElapsed(order.status === "in_progress" ? order.started_at : null);
  const checkedCount = (order.ingredients || []).filter((i) => i.checked).length;
  const totalCount = (order.ingredients || []).length;
  const isCompleted = order.status === "completed";
  const isCancelled = order.status === "cancelled";
  const isAborted = order.status === "aborted";
  const isInProgress = order.status === "in_progress";
  const isPending = order.status === "pending";
  const canDelete = isPending || isCancelled || isAborted;
  const canAbort = isPending || isInProgress;
  const [busy, setBusy] = useState(false);

  const priorityMap = {
    urgent: { label: "🔥 Urgent", cls: "bg-rose-500/30 text-rose-100 border-rose-500/50" },
    normal: { label: "Normale", cls: "bg-slate-700/60 text-slate-300 border-slate-600" },
    low: { label: "Basse", cls: "bg-slate-800/80 text-slate-400 border-slate-700" },
  };
  const p = priorityMap[order.priority] || priorityMap.normal;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(
      `Supprimer définitivement ce bon ?\n\n${order.dish_name}\n\n` +
      (order.caisse_table_id ? "⚠️ Le bon associé côté Caisse sera AUSSI supprimé (cascade)." : "")
    )) return;
    setBusy(true);
    try {
      const cascade = !!order.caisse_table_id;
      await axios.delete(`${API}/kitchen-orders/${order.id}`, {
        params: { actor_role: currentUser?.role || "cuisinier", cascade_table: cascade },
      });
      toast.success(cascade ? "Bon supprimé (Cuisine + Caisse)" : "Bon supprimé");
      onDeleted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const handleAbort = async (e) => {
    e.stopPropagation();
    const reason = window.prompt(
      `Arrêter la préparation de ce bon SANS déstocker ?\n\n${order.dish_name}\n\n` +
      `Motif d'archivage (obligatoire) :`,
      "",
    );
    if (reason === null) return; // annulé
    if (!reason.trim()) {
      toast.error("Motif obligatoire");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/kitchen-orders/${order.id}/abort`, {
        actor_role: currentUser?.role || "cuisinier",
        actor_name: currentUser?.full_name || currentUser?.username || "Cuisinier",
        reason: reason.trim(),
      });
      toast.success("Bon arrêté et archivé (stock inchangé)");
      onAborted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const dishQty = Number(dish.quantity || 1);
  const batchInfo = order.batch_size > 1 ? ` × ${order.batch_size}` : "";
  const showBonTag = totalDishes > 1;

  return (
    <div
      data-testid={`dish-bubble-${order.id}-${dishIndex}`}
      className={`relative rounded-3xl border-2 p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl group
        ${isCompleted
          ? "bg-gradient-to-br from-emerald-950/60 via-emerald-900/30 to-slate-900/90 border-emerald-500/40 hover:border-emerald-400/70"
          : isCancelled
            ? "bg-slate-900/70 border-slate-700 opacity-70"
            : isAborted
              ? "bg-gradient-to-br from-slate-900/80 to-slate-900/60 border-slate-600 opacity-80"
              : isInProgress
                ? "bg-gradient-to-br from-cyan-950/60 via-cyan-900/25 to-slate-900/90 border-cyan-500/50 hover:border-cyan-400/70 ring-2 ring-cyan-500/10"
                : "bg-gradient-to-br from-amber-950/60 via-amber-900/25 to-slate-900/90 border-amber-500/50 hover:border-amber-400/70 ring-2 ring-amber-500/10"
        }`}
    >
      {/* Zone cliquable principale */}
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left cursor-pointer"
        data-testid={`dish-open-${order.id}-${dishIndex}`}
      >
        <div className="flex items-start justify-between gap-2 mb-2 pr-20">
          <div className="min-w-0 flex-1">
            {/* Nom du plat (dish) */}
            <h3 className="text-white font-bold text-base truncate flex items-center gap-1.5">
              <Utensils className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="truncate">{dish.name}</span>
              <span className="text-slate-400 font-mono text-sm flex-shrink-0">×{dishQty}{batchInfo}</span>
            </h3>

            {/* Badges contextuels */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {showBonTag && (
                <Badge className="bg-violet-500/25 text-violet-100 border border-violet-500/50 text-[10px] font-bold">
                  Bon #{(order.id || "").slice(-4)} · Plat {dishIndex + 1}/{totalDishes}
                </Badge>
              )}
              {order.table_no && (
                <Badge className="bg-blue-500/20 text-blue-200 border border-blue-500/40 text-[10px]">
                  🍽 Table {order.table_no}
                </Badge>
              )}
              {order.server_name && (
                <Badge className="bg-slate-700/60 text-slate-300 border border-slate-600 text-[10px] truncate max-w-[100px]" title={order.server_name}>
                  {order.server_name}
                </Badge>
              )}
              {order.auto_created && (
                <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[9px]" title="Auto-créé depuis la Caisse">
                  ⚡ Auto
                </Badge>
              )}
              <Badge className={`text-[10px] border ${p.cls}`}>{p.label}</Badge>
              {isAborted && (
                <Badge className="bg-rose-500/20 text-rose-200 border border-rose-500/40 text-[10px] font-bold">
                  <Archive className="w-2.5 h-2.5 mr-0.5" /> Archivé
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Ingrédients progress (partagé au niveau du bon) */}
        {totalCount > 0 && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
              <span>Ingrédients (bon)</span>
              <span className="font-mono">{checkedCount}/{totalCount}</span>
            </div>
            <div className="h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${
                  isCompleted ? "bg-emerald-500" : isAborted ? "bg-slate-500" : checkedCount === totalCount ? "bg-cyan-500" : "bg-amber-500"
                }`}
                style={{ width: `${totalCount > 0 ? (checkedCount / totalCount) * 100 : 0}%` }}
              />
            </div>
          </div>
        )}

        {/* Chrono ou motif d'archivage */}
        <div className="mt-3 flex items-center justify-between text-[11px]">
          <div className="text-slate-400 flex items-center gap-1">
            <Timer className="w-3 h-3" />
            {isInProgress && elapsed !== null ? (
              <span className="text-cyan-300 font-mono font-bold animate-pulse">{formatTime(elapsed)}</span>
            ) : isCompleted && order.prep_time_seconds ? (
              <span className="text-emerald-300 font-mono">{formatTime(order.prep_time_seconds)}</span>
            ) : (
              <span className="text-slate-500">—</span>
            )}
          </div>
          {order.cook_name && (
            <div className="text-slate-400 flex items-center gap-1 truncate max-w-[50%]">
              <ChefHat className="w-3 h-3 flex-shrink-0" /> {order.cook_name}
            </div>
          )}
        </div>

        {/* 🆕 Motif d'archivage visible pour bons aborted */}
        {isAborted && order.abort_reason && (
          <div className="mt-3 p-2 bg-rose-500/10 border border-rose-500/30 rounded-lg text-[11px] text-rose-100" data-testid={`abort-reason-${order.id}-${dishIndex}`}>
            <div className="flex items-center gap-1 text-rose-300 font-bold uppercase tracking-wider text-[9px] mb-1">
              <Archive className="w-3 h-3" /> Motif d&apos;archivage
            </div>
            <p className="italic">« {order.abort_reason} »</p>
            {order.aborted_by && (
              <p className="text-rose-300/70 text-[9px] mt-1">— {order.aborted_by}</p>
            )}
          </div>
        )}
      </button>

      {/* Boutons flottants — s'affichent une seule fois par bon (sur le 1er plat uniquement pour éviter la duplication) */}
      {dishIndex === 0 && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
          {canAbort && (
            <button
              type="button"
              onClick={handleAbort}
              disabled={busy}
              className="w-8 h-8 rounded-full bg-amber-500/15 hover:bg-amber-500/40 border border-amber-500/40 hover:border-amber-400 text-amber-300 hover:text-white flex items-center justify-center transition-all opacity-80 group-hover:opacity-100 shadow-lg"
              title="Arrêter la préparation et archiver (sans déstocker)"
              data-testid={`abort-order-${order.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <StopCircle className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={busy}
              className="w-8 h-8 rounded-full bg-rose-500/15 hover:bg-rose-500/40 border border-rose-500/40 hover:border-rose-400 text-rose-300 hover:text-white flex items-center justify-center transition-all opacity-70 group-hover:opacity-100 shadow-lg"
              title={order.caisse_table_id ? "Supprimer (cascade Caisse)" : "Supprimer ce bon"}
              data-testid={`kitchen-order-delete-${order.id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ==================== CARD ====================
const OrderCard = ({ order, onOpen, currentUser, onDeleted }) => {
  const elapsed = useLiveElapsed(order.status === "in_progress" ? order.started_at : null);
  const checkedCount = (order.ingredients || []).filter((i) => i.checked).length;
  const totalCount = (order.ingredients || []).length;
  const isCompleted = order.status === "completed";
  const isCancelled = order.status === "cancelled";
  const isInProgress = order.status === "in_progress";
  const isPending = order.status === "pending";
  const canDelete = isPending || isCancelled;
  const [deleting, setDeleting] = useState(false);

  const priorityMap = {
    urgent: { label: "🔥 Urgent", cls: "bg-rose-500/30 text-rose-100 border-rose-500/50" },
    normal: { label: "Normale", cls: "bg-slate-700/60 text-slate-300 border-slate-600" },
    low: { label: "Basse", cls: "bg-slate-800/80 text-slate-400 border-slate-700" },
  };
  const p = priorityMap[order.priority] || priorityMap.normal;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(
      `Supprimer définitivement ce bon ?\n\n${order.dish_name}\n\n` +
      (order.caisse_table_id
        ? "⚠️ Le bon associé côté Caisse sera AUSSI supprimé (cascade)."
        : "Le bon sera retiré de la liste.")
    )) return;
    setDeleting(true);
    try {
      const cascade = !!order.caisse_table_id;
      await axios.delete(`${API}/kitchen-orders/${order.id}`, {
        params: {
          actor_role: currentUser?.role || "cuisinier",
          cascade_table: cascade,
        },
      });
      toast.success(cascade ? "Bon supprimé (Cuisine + Caisse)" : "Bon supprimé");
      if (onDeleted) onDeleted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      data-testid={`kitchen-order-card-${order.id}`}
      className={`relative rounded-3xl border-2 p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-2xl group
        ${isCompleted
          ? "bg-gradient-to-br from-emerald-950/60 via-emerald-900/30 to-slate-900/90 border-emerald-500/40 hover:border-emerald-400/70 hover:shadow-emerald-500/30"
          : isCancelled
            ? "bg-slate-900/70 border-slate-700 opacity-70"
            : isInProgress
              ? "bg-gradient-to-br from-cyan-950/60 via-cyan-900/25 to-slate-900/90 border-cyan-500/50 hover:border-cyan-400/70 hover:shadow-cyan-500/30 ring-2 ring-cyan-500/10"
              : "bg-gradient-to-br from-amber-950/60 via-amber-900/25 to-slate-900/90 border-amber-500/50 hover:border-amber-400/70 hover:shadow-amber-500/30 ring-2 ring-amber-500/10"
        }`}
    >
      {/* Zone cliquable principale (tout sauf le bouton delete) */}
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left cursor-pointer"
        data-testid={`kitchen-order-open-${order.id}`}
      >
      <div className="flex items-start justify-between gap-2 mb-2 pr-8">
        <div className="min-w-0 flex-1">
          <h3 className="text-white font-bold text-base truncate">{order.dish_name}</h3>
          {order.dish_summary && (
            <p className="text-slate-300 text-xs truncate mt-0.5">{order.dish_summary}</p>
          )}
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {order.table_no && (
              <Badge className="bg-blue-500/20 text-blue-200 border border-blue-500/40 text-[10px]">
                🍽 Table {order.table_no}
              </Badge>
            )}
            {order.server_name && (
              <Badge className="bg-slate-700/60 text-slate-300 border border-slate-600 text-[10px] truncate max-w-[110px]" title={order.server_name}>
                {order.server_name}
              </Badge>
            )}
            {order.auto_created && (
              <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[9px]" title="Auto-créé depuis la Caisse">
                ⚡ Auto
              </Badge>
            )}
            {order.batch_size > 1 && (
              <Badge className="bg-indigo-500/20 text-indigo-200 border border-indigo-500/40 text-[10px]">
                <Copy className="w-2.5 h-2.5 mr-0.5" /> x{order.batch_size}
              </Badge>
            )}
            <Badge className={`text-[10px] border ${p.cls}`}>{p.label}</Badge>
          </div>
        </div>
        {isCompleted && order.photo_url && (
          <img src={order.photo_url} alt="" className="w-12 h-12 rounded-lg object-cover border border-emerald-500/40" />
        )}
      </div>

      {/* Ingrédients progress */}
      {totalCount > 0 && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1">
            <span>Ingrédients cochés</span>
            <span className="font-mono">{checkedCount}/{totalCount}</span>
          </div>
          <div className="h-1.5 bg-slate-800/80 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                isCompleted ? "bg-emerald-500" : checkedCount === totalCount ? "bg-cyan-500" : "bg-amber-500"
              }`}
              style={{ width: `${totalCount > 0 ? (checkedCount / totalCount) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Chrono live ou temps final */}
      <div className="mt-3 flex items-center justify-between text-[11px]">
        <div className="text-slate-400 flex items-center gap-1">
          <Timer className="w-3 h-3" />
          {isInProgress && elapsed !== null ? (
            <span className="text-cyan-300 font-mono font-bold animate-pulse">{formatTime(elapsed)}</span>
          ) : isCompleted && order.prep_time_seconds ? (
            <span className="text-emerald-300 font-mono">{formatTime(order.prep_time_seconds)}</span>
          ) : (
            <span className="text-slate-500">—</span>
          )}
        </div>
        {order.cook_name && (
          <div className="text-slate-400 flex items-center gap-1 truncate max-w-[50%]">
            <ChefHat className="w-3 h-3 flex-shrink-0" /> {order.cook_name}
          </div>
        )}
      </div>
      </button>

      {/* Bouton Supprimer — en haut à droite, distinct de la zone cliquable */}
      {canDelete && (
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-rose-500/15 hover:bg-rose-500/40 border border-rose-500/40 hover:border-rose-400 text-rose-300 hover:text-white flex items-center justify-center transition-all opacity-70 group-hover:opacity-100 shadow-lg"
          title={order.caisse_table_id ? "Supprimer (cascade sur la Caisse)" : "Supprimer ce bon"}
          data-testid={`kitchen-order-delete-${order.id}`}
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" strokeWidth={2.5} />}
        </button>
      )}
    </div>
  );
};


// ==================== DETAIL DIALOG ====================
const OrderDetailDialog = ({ order, onClose, onChanged, currentUser, products = [] }) => {
  const [busy, setBusy] = useState(false);
  const [photoData, setPhotoData] = useState(null); // base64
  const fileInputRef = useRef(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ product_id: "", quantity: "1", check_now: false });
  const [addSearch, setAddSearch] = useState("");
  const elapsed = useLiveElapsed(order?.status === "in_progress" ? order.started_at : null);

  if (!order) return null;
  const isCompleted = order.status === "completed";
  const isCancelled = order.status === "cancelled";
  const isAborted = order.status === "aborted";
  const isPending = order.status === "pending";
  const isInProgress = order.status === "in_progress";
  const isAdmin = currentUser?.role === "admin";
  const canEdit = !isCompleted && !isCancelled && !isAborted;
  const canAbort = isPending || isInProgress;

  const startOrder = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kitchen-orders/${order.id}/start`, {
        cook_name: currentUser?.full_name || currentUser?.username || "Cuisinier",
        cook_id: currentUser?.id,
      });
      toast.success("Bon commencé — chrono démarré ⏱");
      onChanged();
      onClose();
      // Ré-ouvre avec la nouvelle version
      setTimeout(() => window.dispatchEvent(new CustomEvent("kitchen-order-updated", { detail: r.data.order })), 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const toggleIngredient = async (idx, checked, actualQty) => {
    try {
      await axios.patch(`${API}/kitchen-orders/${order.id}/toggle-ingredient`, {
        index: idx, checked, actual_qty: actualQty !== undefined ? parseFloat(actualQty) : undefined,
      });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const removeIngredient = async (idx) => {
    const ing = order.ingredients?.[idx];
    if (!ing) return;
    if (!window.confirm(`Retirer "${ing.product_name}" du bon ?`)) return;
    try {
      await axios.delete(`${API}/kitchen-orders/${order.id}/ingredient/${idx}`);
      toast.success("Ingrédient retiré");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const addIngredient = async () => {
    const qty = parseFloat(String(addForm.quantity).replace(",", "."));
    if (!addForm.product_id) { toast.error("Sélectionne un produit"); return; }
    if (isNaN(qty) || qty <= 0) { toast.error("Quantité invalide"); return; }
    const product = products.find((p) => p.id === addForm.product_id);
    if (!product) { toast.error("Produit introuvable"); return; }
    try {
      await axios.post(`${API}/kitchen-orders/${order.id}/add-ingredient`, {
        product_id: addForm.product_id,
        product_name: product.name,
        quantity: qty,
        unit: product.unit || "",
        check_now: addForm.check_now,
      });
      toast.success(`✓ ${product.name} ajouté (${qty} ${product.unit || ""})`);
      setAddOpen(false);
      setAddForm({ product_id: "", quantity: "1", check_now: false });
      setAddSearch("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { // 500KB max after compression
      toast.warning("Photo trop lourde — sélectionnez une plus petite (max 500 KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Compress via canvas (max 800px)
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(800 / img.width, 800 / img.height, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setPhotoData(canvas.toDataURL("image/jpeg", 0.75));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const completeOrder = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kitchen-orders/${order.id}/complete`, {
        cook_name: currentUser?.full_name || currentUser?.username || "Cuisinier",
        photo_url: photoData || null,
      });
      const decCount = r.data?.decremented_count || 0;
      const prep = r.data?.prep_time_seconds;
      toast.success(
        <div>
          <div><Trophy className="w-4 h-4 inline mr-1 text-amber-400" /> Bon terminé !</div>
          <div className="text-xs mt-1">
            {decCount} produit{decCount > 1 ? "s" : ""} décrémenté{decCount > 1 ? "s" : ""} du stock
            {prep ? ` · ${formatTime(prep)}` : ""}
          </div>
        </div>
      );
      setPhotoData(null);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant la validation");
    } finally { setBusy(false); }
  };

  const cancelCompleted = async () => {
    if (!isAdmin) return;
    const reason = window.prompt("Motif de l'annulation ?\n\n(Les mouvements de stock seront inversés automatiquement)");
    if (reason === null) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kitchen-orders/${order.id}/cancel-completed`, {
        actor_role: "admin",
        actor_name: currentUser?.full_name || currentUser?.username || "Admin",
        reason,
      });
      toast.success(`Bon annulé — ${r.data?.restored_movements || 0} mouvement(s) inversé(s)`);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  // 🆕 (Fév 2026) Suppression d'un bon par le cuisinier (ou tout rôle habilité)
  const deleteOrder = async () => {
    if (!window.confirm(
      `Supprimer définitivement ce bon ?\n\n${order.dish_name}\n\n` +
      (order.caisse_table_id
        ? "⚠️ Le bon associé côté Caisse sera AUSSI supprimé (cascade)."
        : "Le bon sera retiré de la liste.")
    )) return;
    setBusy(true);
    try {
      const cascade = !!order.caisse_table_id;
      await axios.delete(`${API}/kitchen-orders/${order.id}`, {
        params: {
          actor_role: currentUser?.role || "cuisinier",
          cascade_table: cascade,
        },
      });
      toast.success(cascade ? "Bon supprimé (côté Cuisine + Caisse)" : "Bon supprimé");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  // 🆕 (Fév 2026) Arrêter la préparation SANS déstocker + archiver avec motif
  const abortOrder = async () => {
    const reason = window.prompt(
      `Arrêter la préparation de ce bon SANS déstocker ?\n\n${order.dish_name}\n\n` +
      `Motif d'archivage (obligatoire) :`,
      "",
    );
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error("Motif obligatoire");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/kitchen-orders/${order.id}/abort`, {
        actor_role: currentUser?.role || "cuisinier",
        actor_name: currentUser?.full_name || currentUser?.username || "Cuisinier",
        reason: reason.trim(),
      });
      toast.success("Bon arrêté et archivé (stock inchangé)");
      onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <>
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-slate-900 border-amber-500/40 max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-amber-200 flex items-center gap-2 flex-wrap">
            <ChefHat className="w-5 h-5" />
            <span className="text-white">{order.dish_name}</span>
            {order.batch_size > 1 && (
              <Badge className="bg-indigo-500/20 text-indigo-200 border border-indigo-500/40 text-xs">
                <Copy className="w-3 h-3 mr-0.5" /> Batch x{order.batch_size}
              </Badge>
            )}
            {order.status === "in_progress" && elapsed !== null && (
              <Badge className="bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 text-xs font-mono animate-pulse ml-auto">
                <Timer className="w-3 h-3 mr-0.5" /> {formatTime(elapsed)}
              </Badge>
            )}
            {isCompleted && order.prep_time_seconds && (
              <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-xs font-mono ml-auto">
                <Timer className="w-3 h-3 mr-0.5" /> {formatTime(order.prep_time_seconds)}
              </Badge>
            )}
          </DialogTitle>
          {order.notes && (
            <DialogDescription className="text-slate-300 italic">
              « {order.notes} »
            </DialogDescription>
          )}
        </DialogHeader>

        {/* Ingrédients */}
        <div className="space-y-1.5" data-testid="ingredients-list">
          {(order.ingredients || []).length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-8 border border-dashed border-slate-700 rounded-lg">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Aucun ingrédient — utilise le bouton <strong className="text-emerald-300">« Ajouter »</strong> ci-dessous.
            </div>
          ) : (order.ingredients || []).map((ing, idx) => (
            <IngredientRow
              key={idx}
              ing={ing}
              idx={idx}
              disabled={!canEdit}
              onToggle={(checked, actual) => toggleIngredient(idx, checked, actual)}
              onRemove={canEdit ? removeIngredient : null}
            />
          ))}
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              className="w-full mt-2 border-dashed border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:border-emerald-500/60"
              data-testid="add-ingredient-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter un ingrédient
            </Button>
          )}
        </div>

        {/* Photo QC (G) — visible si terminé OU si on est sur le point de terminer */}
        {(order.status === "in_progress" || isCompleted) && (
          <div className="mt-2 pt-3 border-t border-slate-800">
            <Label className="text-slate-300 text-xs uppercase font-bold flex items-center gap-1">
              <Camera className="w-3.5 h-3.5" /> Photo QC (contrôle qualité)
            </Label>
            {isCompleted && order.photo_url ? (
              <img src={order.photo_url} alt="Photo QC" className="mt-2 rounded-lg border border-emerald-500/40 max-h-48 object-cover" />
            ) : photoData ? (
              <div className="mt-2 relative inline-block">
                <img src={photoData} alt="Preview" className="rounded-lg border border-amber-500/40 max-h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => setPhotoData(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              order.status === "in_progress" && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoChange}
                    className="hidden"
                    data-testid="photo-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="mt-2 border-slate-600 text-slate-300 hover:bg-slate-800"
                    data-testid="photo-btn"
                  >
                    <Camera className="w-4 h-4 mr-1" /> Ajouter une photo
                  </Button>
                </>
              )
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t border-slate-800 mt-2 flex-wrap">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300">
            Fermer
          </Button>
          {/* 🆕 Supprimer (dispo cuisinier + admin/manager) — pending ou cancelled/aborted uniquement */}
          {(isPending || isCancelled || isAborted) && (
            <Button
              variant="outline"
              onClick={deleteOrder}
              disabled={busy}
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              data-testid="delete-order-btn"
              title={order.caisse_table_id ? "Supprime aussi le bon côté Caisse" : "Retire ce bon de la liste"}
            >
              <X className="w-4 h-4 mr-1" /> Supprimer
            </Button>
          )}
          {/* 🆕 Arrêter la préparation SANS déstocker + archiver avec motif */}
          {canAbort && (
            <Button
              variant="outline"
              onClick={abortOrder}
              disabled={busy}
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              data-testid="abort-order-btn"
              title="Arrêter la préparation et archiver — le stock n'est PAS décrémenté"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <StopCircle className="w-4 h-4 mr-1" />}
              Arrêter et archiver
            </Button>
          )}
          <div className="flex-1" />
          {isPending && (
            <Button onClick={startOrder} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="start-order-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-1" />}
              Commencer la préparation
            </Button>
          )}
          {order.status === "in_progress" && (
            <Button onClick={completeOrder} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="complete-order-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Terminer et décrémenter le stock
            </Button>
          )}
          {isCompleted && isAdmin && (
            <Button onClick={cancelCompleted} disabled={busy} variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10" data-testid="cancel-completed-btn">
              <X className="w-4 h-4 mr-1" /> Annuler (admin)
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* 🆕 Dialog secondaire : Ajout d'un ingrédient (Fév 2026) */}
    <Dialog open={addOpen} onOpenChange={setAddOpen}>
      <DialogContent className="bg-slate-900 border-emerald-500/40 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-emerald-200 flex items-center gap-2">
            <Plus className="w-5 h-5" /> Ajouter un ingrédient
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Choisis un produit du stock et la quantité utilisée.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-slate-300 text-xs uppercase font-bold">Rechercher un produit</Label>
            <Input
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              placeholder="Ex: tomate, poulet…"
              className="bg-slate-800 border-slate-700 text-white mt-1"
              autoFocus
              data-testid="add-ingredient-search"
            />
          </div>
          <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/50">
            {products
              .filter((p) => !addSearch || (p.name || "").toLowerCase().includes(addSearch.toLowerCase()))
              .slice(0, 40)
              .map((p) => {
                const active = p.id === addForm.product_id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setAddForm((f) => ({ ...f, product_id: p.id }))}
                    className={`w-full text-left px-3 py-1.5 text-sm border-b border-slate-800/50 last:border-b-0 transition-colors ${
                      active ? "bg-emerald-500/20 text-emerald-100" : "text-slate-300 hover:bg-slate-800/70"
                    }`}
                    data-testid={`add-product-${p.id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate flex-1">{p.name}</span>
                      <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
                        stock {p.quantity ?? 0} {p.unit}
                      </span>
                    </div>
                  </button>
                );
              })}
            {products.length === 0 && (
              <p className="text-slate-500 text-xs text-center py-4">Aucun produit chargé</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <Label className="text-slate-300 text-xs uppercase font-bold">Quantité utilisée</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={addForm.quantity}
                onChange={(e) => setAddForm((f) => ({ ...f, quantity: e.target.value }))}
                className="bg-slate-800 border-slate-700 text-white mt-1 font-mono"
                data-testid="add-quantity-input"
              />
            </div>
            <label className="flex items-center gap-2 text-slate-300 text-xs bg-slate-800/40 border border-slate-700 rounded-lg p-2 cursor-pointer">
              <input
                type="checkbox"
                checked={addForm.check_now}
                onChange={(e) => setAddForm((f) => ({ ...f, check_now: e.target.checked }))}
                className="rounded"
                data-testid="add-check-now"
              />
              Cocher tout de suite
            </label>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={() => setAddOpen(false)} className="flex-1 border-slate-600 text-slate-300">
            Annuler
          </Button>
          <Button
            onClick={addIngredient}
            disabled={!addForm.product_id}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-ingredient-submit"
          >
            <Plus className="w-4 h-4 mr-1" /> Ajouter
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};


// ==================== INGREDIENT ROW ====================
const IngredientRow = ({ ing, idx, disabled, onToggle, onRemove }) => {
  const [actualQty, setActualQty] = useState(String(ing.actual_qty ?? ing.expected_qty ?? 0));
  useEffect(() => { setActualQty(String(ing.actual_qty ?? ing.expected_qty ?? 0)); }, [ing.actual_qty, ing.expected_qty]);

  const expected = parseFloat(ing.expected_qty) || 0;
  const actual = parseFloat(actualQty) || 0;
  const variance = actual - expected;
  const isManuallyAdded = ing.added_manually;

  const handleRowClick = (e) => {
    // Ignore les clics dans l'input (édition qty)
    if (e.target.tagName === "INPUT" || e.target.closest("button, input")) return;
    if (disabled) return;
    onToggle(!ing.checked, actual);
  };

  return (
    <div
      onClick={handleRowClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onToggle(!ing.checked, actual); } }}
      className={`flex items-center gap-2.5 p-3 rounded-lg border transition-all select-none ${
        ing.checked
          ? "bg-emerald-500/10 border-emerald-500/40 shadow-inner"
          : "bg-slate-800/40 border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800/70"
      } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer active:scale-[0.99]"}`}
      data-testid={`ingredient-${idx}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); if (!disabled) onToggle(!ing.checked, actual); }}
        disabled={disabled}
        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
          ing.checked
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "border-slate-500 hover:border-emerald-400"
        }`}
        data-testid={`toggle-ingredient-${idx}`}
        aria-label={ing.checked ? "Décocher" : "Cocher"}
      >
        {ing.checked && <CheckCircle2 className="w-4 h-4" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`text-sm ${ing.checked ? "text-emerald-50" : "text-white"} truncate font-medium flex items-center gap-1.5`}>
          {ing.product_name}
          {isManuallyAdded && (
            <Badge className="bg-violet-500/25 text-violet-100 border border-violet-500/50 text-[9px] px-1.5 py-0" title="Ajouté manuellement">
              + Manuel
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        <Input
          type="number"
          step="0.01"
          value={actualQty}
          onChange={(e) => setActualQty(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={() => {
            if (parseFloat(actualQty) !== parseFloat(ing.actual_qty) && ing.checked) {
              onToggle(true, parseFloat(actualQty));
            }
          }}
          disabled={disabled}
          className="w-20 h-8 bg-slate-900 border-slate-700 text-white text-sm font-mono text-right"
          data-testid={`qty-input-${idx}`}
        />
        <span className="text-slate-500 text-xs w-12">{ing.unit}</span>
        {ing.checked && Math.abs(variance) > 0.001 && (
          <Badge className={`text-[10px] ${
            variance > 0
              ? "bg-orange-500/20 text-orange-200 border border-orange-500/40"
              : "bg-cyan-500/20 text-cyan-200 border border-cyan-500/40"
          }`}>
            {variance > 0 ? "+" : ""}{variance.toFixed(2)}
          </Badge>
        )}
        {!disabled && onRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(idx); }}
            className="w-7 h-7 rounded-full flex items-center justify-center text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"
            title="Retirer cet ingrédient"
            data-testid={`remove-ingredient-${idx}`}
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};


export default FicheTechniqueTab;
